/**
 * ─────────────────────────────────────────────────────────────────────────────
 * AgroCast — Theme Utilities
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Pure functions that handle:
 *  - CSS variable injection into :root
 *  - Tailwind `dark` class toggling on <html>
 *  - System preference detection + media query listening
 *  - LocalStorage persistence
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { lightTokens, darkTokens, staticTokens, THEMES } from './tokens'

// ── Constants ─────────────────────────────────────────────────────────────────
export const STORAGE_KEY    = 'agrocast_theme'
export const CSS_VAR_PREFIX = '--ac'
export const DARK_CLASS     = 'dark'

// ── CSS Variable Injection ────────────────────────────────────────────────────

/**
 * Converts a token map to CSS custom properties and sets them on <html>.
 * Token keys like "bg-base" become "--ac-bg-base".
 */
export function injectCssVars(tokens) {
  const root = document.documentElement
  Object.entries(tokens).forEach(([key, value]) => {
    root.style.setProperty(`${CSS_VAR_PREFIX}-${key}`, value)
  })
}

/**
 * Applies the full theme: CSS vars + Tailwind dark class.
 * @param {'light' | 'dark'} resolvedTheme — already resolved (no 'system')
 */
export function applyTheme(resolvedTheme) {
  const root      = document.documentElement
  const semantics = resolvedTheme === THEMES.DARK ? darkTokens : lightTokens

  // 1. Inject semantic tokens (theme-dependent)
  injectCssVars(semantics)

  // 2. Inject static tokens (same for both themes) — idempotent
  injectCssVars(staticTokens)

  // 3. Tailwind dark mode class
  if (resolvedTheme === THEMES.DARK) {
    root.classList.add(DARK_CLASS)
    root.setAttribute('data-theme', THEMES.DARK)
  } else {
    root.classList.remove(DARK_CLASS)
    root.setAttribute('data-theme', THEMES.LIGHT)
  }

  // 4. Color-scheme for native UI elements (scrollbars, inputs, etc.)
  root.style.colorScheme = resolvedTheme
}

// ── System Preference ─────────────────────────────────────────────────────────

/**
 * Returns the user's OS-level color scheme preference.
 */
export function getSystemTheme() {
  if (typeof window === 'undefined') return THEMES.LIGHT
  return window.matchMedia('(prefers-color-scheme: dark)').matches
    ? THEMES.DARK
    : THEMES.LIGHT
}

/**
 * Subscribes to system theme changes.
 * Returns an unsubscribe function.
 */
export function subscribeToSystemTheme(callback) {
  const mq = window.matchMedia('(prefers-color-scheme: dark)')
  const handler = (e) => callback(e.matches ? THEMES.DARK : THEMES.LIGHT)
  mq.addEventListener('change', handler)
  return () => mq.removeEventListener('change', handler)
}

// ── LocalStorage Persistence ──────────────────────────────────────────────────

export function persistTheme(theme) {
  try {
    localStorage.setItem(STORAGE_KEY, theme)
  } catch {
    // localStorage may be blocked in some contexts
  }
}

export function getPersistedTheme() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored && Object.values(THEMES).includes(stored)) return stored
  } catch {
    // ignore
  }
  return null
}

// ── Theme Resolution ──────────────────────────────────────────────────────────

/**
 * Resolves a theme preference (including 'system') to an actual 'light'|'dark'.
 */
export function resolveTheme(preference) {
  if (preference === THEMES.SYSTEM) return getSystemTheme()
  return preference
}

// ── CSS Variable Reader ───────────────────────────────────────────────────────

/**
 * Read a CSS variable value at runtime. Useful for canvas/SVG operations.
 * @param {string} token — e.g. 'primary', 'text-base'
 */
export function getCssVar(token) {
  return getComputedStyle(document.documentElement)
    .getPropertyValue(`${CSS_VAR_PREFIX}-${token}`)
    .trim()
}

// ── Blocking Script (FOUC prevention) ────────────────────────────────────────

/**
 * Returns a stringified script to be injected into <head> BEFORE React loads.
 * Prevents flash of wrong theme on first paint.
 */
export function getAntiFlashScript() {
  return `
(function() {
  try {
    var stored = localStorage.getItem('${STORAGE_KEY}');
    var pref   = stored || 'system';
    var isDark = pref === 'dark' ||
      (pref === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
    if (isDark) {
      document.documentElement.classList.add('${DARK_CLASS}');
      document.documentElement.setAttribute('data-theme', 'dark');
      document.documentElement.style.colorScheme = 'dark';
    } else {
      document.documentElement.setAttribute('data-theme', 'light');
      document.documentElement.style.colorScheme = 'light';
    }
  } catch(e) {}
})();
`.trim()
}

// ── Tailwind Class Helpers ────────────────────────────────────────────────────

/**
 * Glass token class strings — use these in component className props.
 * Returns the correct Tailwind classes for the current theme.
 */
export const glassClasses = {
  light: [
    'backdrop-blur-xl',
    'bg-white/60',
    'border border-white/20',
    'shadow-xl',
  ].join(' '),

  dark: [
    'backdrop-blur-xl',
    'bg-slate-900/60',
    'border border-slate-700/40',
    'shadow-xl',
  ].join(' '),
}

/**
 * Returns the correct glass classes for a given resolved theme.
 */
export function getGlassClasses(resolvedTheme) {
  return resolvedTheme === THEMES.DARK ? glassClasses.dark : glassClasses.light
}
